package com.schoolconnect.app.feature.homework

import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.compareBySmartText

data class HomeworkFilters(
    val className: String = "",
    val classId: String = "",
    val subject: String = "",
    val subjectId: String = "",
    val status: String = ""
)

data class HomeworkRowUi(
    val backendId: String,
    val schoolId: String,
    val classSectionId: String,
    val subjectId: String,
    val homeworkId: String,
    val title: String,
    val className: String,
    val subject: String,
    val assignedBy: String,
    val assignedByTeacherId: String = "",
    val assignedDate: String,
    val updatedDate: String = "",
    val dueDate: String,
    val status: String,
    val notifyParents: Boolean? = null,
    val description: String,
    val attachmentFileIds: List<String> = emptyList(),
    val attachmentFileUrls: List<String> = emptyList()
)

data class HomeworkAttachmentMetadataUi(
    val fileId: String,
    val name: String,
    val type: String,
    val sizeLabel: String,
    val isImage: Boolean,
    val previewBytes: ByteArray? = null
)

data class HomeworkDraft(
    val homeworkId: String? = null,
    val schoolId: String? = null,
    val title: String,
    val classSectionId: String,
    val subjectId: String,
    val dueDate: String,
    val description: String,
    val assignedByTeacherId: String? = null,
    val attachmentFileIds: List<String> = emptyList()
)

data class HomeworkUiState(
    val isLoading: Boolean = true,
    val errorMessage: String = "",
    val saveErrorMessage: String = "",
    val searchQuery: String = "",
    val filters: HomeworkFilters = HomeworkFilters(),
    val rows: List<HomeworkRowUi> = emptyList(),
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val totalCount: Int = 0,
    val totalHomework: Int = 0,
    val assignedByMe: Int = 0,
    val allOpen: Int = 0,
    val closed: Int = 0,
    val overdue: Int = 0,
    val isSaving: Boolean = false,
    val saveGeneration: Int = 0,
    val viewingHomework: HomeworkRowUi? = null,
    val isViewingHomeworkLoading: Boolean = false,
    val viewingHomeworkError: String = "",
    val editingHomework: HomeworkRowUi? = null,
    val isEditingHomeworkLoading: Boolean = false,
    val editingHomeworkError: String = "",
    val classOptionIds: Map<String, String> = emptyMap(),
    val subjectOptionIds: Map<String, String> = emptyMap(),
    val subjectOptionIdsByClassId: Map<String, Map<String, String>> = emptyMap(),
    val statusOptions: List<String> = emptyList(),
    val teacherOptionIds: Map<String, String> = emptyMap(),
    val teacherOptionIdsByClassSubject: Map<String, Map<String, String>> = emptyMap(),
    val requiresTeacherSelection: Boolean = false,
    val sortField: HomeworkSortField = HomeworkSortField.ID,
    val sortAscending: Boolean = true,
    val attachmentMetadata: Map<String, HomeworkAttachmentMetadataUi> = emptyMap(),
    val pendingDelete: HomeworkRowUi? = null,
    val isDeleting: Boolean = false,
    val pendingClose: HomeworkRowUi? = null,
    val isClosing: Boolean = false
) {
    val classOptions: List<String> get() = classOptionIds.keys
        .filter { it.isNotBlank() && it != "—" }
        .sortedWith(compareBySmartText { it })
}

sealed interface HomeworkUiEvent {
    data object BackClicked : HomeworkUiEvent
    data object AddRequested : HomeworkUiEvent
    data object RefreshClicked : HomeworkUiEvent
    data class SearchChanged(val query: String) : HomeworkUiEvent
    data class FiltersChanged(val filters: HomeworkFilters) : HomeworkUiEvent
    data object FiltersReset : HomeworkUiEvent
    data class PageChanged(val pageIndex: Int) : HomeworkUiEvent
    data class PageSizeChanged(val pageSize: Int) : HomeworkUiEvent
    data class SortChanged(val field: HomeworkSortField) : HomeworkUiEvent
    data class ViewRequested(val homework: HomeworkRowUi) : HomeworkUiEvent
    data object ViewDismissed : HomeworkUiEvent
    data class EditRequested(val homework: HomeworkRowUi) : HomeworkUiEvent
    data object EditDismissed : HomeworkUiEvent
    data class AttachmentMetadataRequested(val homework: HomeworkRowUi) : HomeworkUiEvent
    data class SaveRequested(val draft: HomeworkDraft) : HomeworkUiEvent
    data class DeleteRequested(val homework: HomeworkRowUi) : HomeworkUiEvent
    data object DeleteConfirmed : HomeworkUiEvent
    data object DeleteDismissed : HomeworkUiEvent
    data class CloseRequested(val homework: HomeworkRowUi) : HomeworkUiEvent
    data object CloseConfirmed : HomeworkUiEvent
    data object CloseDismissed : HomeworkUiEvent
    data class ReminderRequested(val homework: HomeworkRowUi) : HomeworkUiEvent
}

enum class HomeworkSortField { ID, TITLE, CLASS, SUBJECT, ASSIGNED_BY, ASSIGNED_DATE, DUE_DATE, STATUS }
